import { type FormEvent, useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { Controller, type Control } from "react-hook-form";
import { ChoiceCard, OptionalMark, RichLabel } from "@patient-watch/forms";
import type { InputT } from "@patient-watch/types";
import {
    Questionnaire,
    QuestionnaireChoice,
    QuestionnaireChoiceDescription,
    QuestionnaireChoices,
    QuestionnaireItem,
} from "@patient-watch/ui/questionnaire";

import { services as s } from "@/services";
import { useEntryValuesStore } from "../../inputs/hooks/useEntryValuesStore";
import PageGroupTotal from "./PageGroupTotal";
import type { PageValidation } from "@patient-watch/forms/pageValidation";

interface Props {
    inputs: InputT[];
    control: Control<Record<string, unknown>>;
    pageValidation: PageValidation | null;
}

/** The questionnaire primitive is its own <form>. Its submit and reset stay here, as in ChoiceGroup. */
function stop(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
}

/**
 * Back-to-back checkbox inputs as one multi-select question, drawn with the shadcn Questionnaire choices
 * like a single-choice question. Each checkbox is still its own true/false input, so the saved answers
 * and the page rules (at least one ticked, a running total) work as before.
 */
const CheckboxGroupCard = ({ inputs, control, pageValidation }: Props) => {
    const { entryId } = useParams({ strict: false }) as { entryId?: string };
    const upsertEntryValue = useEntryValuesStore((state) => state.upsertEntryValue);

    // Every checkbox gets a saved row with its own id up front, as InputSwitch does for any other input.
    // Without it the save sends these answers with no entry_value_id beside rows that have one, and the
    // bulk upsert fills the gap with null (not-null violation on entry_values.entry_value_id).
    useEffect(() => {
        if (!entryId) return;
        for (const input of inputs) {
            const existing = useEntryValuesStore.getState().getEntryValueByInputId(input.input_id);
            if (existing?.entry_value_id) continue;
            upsertEntryValue(
                existing
                    ? { ...existing, entry_value_id: s.uuidv4() }
                    : {
                          entry_id: entryId,
                          input_id: input.input_id,
                          entry_value_id: s.uuidv4(),
                          number_value: null,
                          text_value: null,
                          text_array: null,
                          json_value: null,
                          object_id: null,
                      }
            );
        }
    }, [entryId, inputs, upsertEntryValue]);

    const first = inputs[0];
    if (!first) return null;

    return (
        <ChoiceCard>
            <Questionnaire onSubmit={stop} onReset={stop}>
                <QuestionnaireItem name={first.input_id} multiple>
                    <QuestionnaireChoices data-testid={`form-options-${first.input_id}`}>
                        {inputs.map((input) => (
                            <div key={input.input_id}>
                                <Controller
                                    control={control}
                                    name={input.input_id}
                                    render={({ field, fieldState }) => (
                                        <QuestionnaireChoice
                                            value={input.input_id}
                                            checked={field.value === true}
                                            onChange={(event) => field.onChange(event.target.checked)}
                                            invalid={fieldState.invalid}
                                            className={input.input_description ? "items-start" : undefined}
                                            data-testid="form-option"
                                            data-input-id={input.input_id}
                                            inputProps={
                                                {
                                                    "data-testid": `form-option-${input.input_id}`,
                                                    ref: field.ref,
                                                    onBlur: field.onBlur,
                                                } as never
                                            }
                                        >
                                            <span>
                                                <RichLabel>{input.input_label ?? ""}</RichLabel>
                                                {input.is_optional && <OptionalMark />}
                                            </span>
                                            {input.input_description ? (
                                                <QuestionnaireChoiceDescription>
                                                    <RichLabel>{input.input_description}</RichLabel>
                                                </QuestionnaireChoiceDescription>
                                            ) : null}
                                        </QuestionnaireChoice>
                                    )}
                                />
                                <PageGroupTotal
                                    validation={pageValidation}
                                    inputId={input.input_id}
                                    control={control}
                                />
                            </div>
                        ))}
                    </QuestionnaireChoices>
                </QuestionnaireItem>
            </Questionnaire>
        </ChoiceCard>
    );
};

export default CheckboxGroupCard;
